import { useEffect, useRef, useState } from 'react';
import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { reportException } from '@/services/sentry.ts';
import { isSSEDisabledForUrl } from '@/hooks/api/sse/utils.ts';

const SENTRY_COOLDOWN_MS = 2 * 60 * 1000;
const PERMANENT_OPEN_FAILURE_STATUS_CODES = new Set([400, 403, 404, 422]);

class PermanentSSEConnectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermanentSSEConnectionError';
  }
}

function backoffMs(attempt: number) {
  return Math.min(500 * 2 ** Math.min(attempt, 5), 10_000);
}

function classifyError(message: string) {
  if (message.includes('401')) return 'auth';
  if (message.includes('content-type')) return 'protocol';
  if (message.includes('closed')) return 'closed';
  return 'error';
}

function isPermanentSSEConnectionError(err: unknown) {
  return err instanceof PermanentSSEConnectionError;
}

function isPermanentOpenFailureStatus(status: number) {
  return PERMANENT_OPEN_FAILURE_STATUS_CODES.has(status);
}

type Params = {
  url: string;
  getAccessToken: () => Promise<string | undefined>;
  onMessage: (msg: EventSourceMessage) => void;
  onConnect?: () => void;
  onUnauthorized?: () => Promise<void>;
  enabled: boolean;
};

export function useSSEConnection({
  url,
  getAccessToken,
  onMessage,
  onConnect,
  onUnauthorized,
  enabled,
}: Params) {
  const [isConnected, setIsConnected] = useState(false);
  const isDisabled = isSSEDisabledForUrl(url);

  const getAccessTokenRef = useRef(getAccessToken);
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onUnauthorizedRef = useRef(onUnauthorized);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onConnectRef.current = onConnect;
  }, [onConnect]);

  useEffect(() => {
    onUnauthorizedRef.current = onUnauthorized;
  }, [onUnauthorized]);

  const controllerRef = useRef<AbortController | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const connectingRef = useRef(false);
  const stoppedRef = useRef(false);

  const lastSentryAtRef = useRef(0);
  const lastSentryKeyRef = useRef('');

  useEffect(() => {
    stoppedRef.current = false;
    retryCountRef.current = 0;

    const clearRetryTimer = () => {
      if (retryTimerRef.current != null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const reportOnceInAWhile = (err: unknown, key: string) => {
      const now = Date.now();
      const sameKey = key === lastSentryKeyRef.current;
      const inCooldown = now - lastSentryAtRef.current < SENTRY_COOLDOWN_MS;
      if (sameKey && inCooldown) return;

      lastSentryKeyRef.current = key;
      lastSentryAtRef.current = now;

      reportException(err, {
        tags: { sse_url: url, sse_key: key },
        extra: { retryCount: retryCountRef.current },
      });
    };

    const stop = () => {
      stoppedRef.current = true;
      connectingRef.current = false;
      clearRetryTimer();
      controllerRef.current?.abort();
      controllerRef.current = null;
      setIsConnected(false);
    };

    const scheduleRetry = (err?: unknown) => {
      if (stoppedRef.current) return;
      if (!navigator.onLine) return;
      if (retryTimerRef.current != null) return;

      if (err) {
        const key = classifyError(String((err as any)?.message || ''));
        reportOnceInAWhile(err, key);
      }

      const delay = backoffMs(retryCountRef.current);
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        retryCountRef.current += 1;
        start();
      }, delay);
    };

    const start = async () => {
      if (!enabled) return;
      if (stoppedRef.current) return;
      if (!navigator.onLine) return;
      if (connectingRef.current) return;

      if (isDisabled) return;

      connectingRef.current = true;
      clearRetryTimer();

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const token = await getAccessTokenRef.current();

        if (!token) return;
        if (stoppedRef.current) return;
        if (controller.signal.aborted) return;

        await fetchEventSource(url, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
          openWhenHidden: true,

          onopen: async (res) => {
            if (!res.ok) {
              if (res.status === 401 && onUnauthorizedRef.current) {
                await onUnauthorizedRef.current();
                if (stoppedRef.current) return;
                retryCountRef.current = 0;
                throw new Error('401');
              }
              if (res.status === 401) {
                throw new PermanentSSEConnectionError('open_401');
              }
              if (isPermanentOpenFailureStatus(res.status)) {
                throw new PermanentSSEConnectionError(`open_${res.status}`);
              }
              throw new Error(`open_${res.status}`);
            }

            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('text/event-stream')) {
              throw new PermanentSSEConnectionError('content-type');
            }

            retryCountRef.current = 0;
            setIsConnected(true);
            onConnectRef.current?.();
          },

          onmessage: (msg) => onMessageRef.current(msg),

          onclose: () => {
            setIsConnected(false);
            scheduleRetry();
          },

          onerror: (err) => {
            throw err; // avoid internal retry loop
          },
        });
      } catch (err: any) {
        if (err?.name === 'AbortError' || stoppedRef.current) return;
        setIsConnected(false);
        if (isPermanentSSEConnectionError(err)) {
          reportOnceInAWhile(err, classifyError(String(err.message || '')));
          return;
        }
        scheduleRetry(err);
      } finally {
        connectingRef.current = false;
      }
    };

    const handleOffline = () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
      setIsConnected(false);
      clearRetryTimer();
    };

    const handleOnline = () => {
      retryCountRef.current = 0;
      clearRetryTimer();
      start();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    start();

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      stop();
    };
  }, [enabled, isDisabled, url]);

  return { isConnected, isDisabled };
}
